import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { USER_APPROVE_STATUS, USER_QUALIFY_STATUS } from '@/config/constants';
import { useGetCommandCenterQuery } from '@/apis/extended/userInterestRateApi';
import {
  buildRateInsightChipLabel,
  RateCommandCenterPanel,
} from '@/components/DocumentsVault/RateCommandCenterPanel';
import { useAuth } from '@/hooks/useAuth';
import { usePurchasingPowerEstimation } from '@/hooks/usePurchasingPowerEstimation';

const ALT_MS = 4000;
const HOVER_CLOSE_MS = 280;
/** Reserved width so header nav does not shift when labels alternate. */
const CHIP_SLOT_WIDTH = 300;
const CHIP_SLOT_HEIGHT = 32;

function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  }
  if (value >= 1_000) {
    return `$${Math.round(value / 1_000)}K`;
  }
  return `$${Math.round(value)}`;
}

export const BuyingPowerChip = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const qualified = Boolean(
    user && user.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY
  );

  const { data: ccData } = useGetCommandCenterQuery(undefined, {
    skip: !user?._id || !qualified,
    pollingInterval: 120_000,
  });

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_MS);
  }, [cancelClose]);

  useEffect(() => {
    if (!qualified) return undefined;
    const id = window.setInterval(() => {
      setPhase((p) => (p + 1) % 2);
    }, ALT_MS);
    return () => clearInterval(id);
  }, [qualified]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const insightShort = useMemo(() => {
    if (ccData) return buildRateInsightChipLabel(ccData);
    return 'Rate command center · hover or click';
  }, [ccData]);

  // The exact figure the pre-qualification page shows (same priced rate from
  // /user/interest-rate and the same PITI/DTI math). The chip used to price
  // its own rate and drifted ~$9K from the pre-qual letter.
  const preQualPower = usePurchasingPowerEstimation();

  const qualifiedBuyingPower = useMemo(() => {
    if (!user || user.qualifyStatus !== USER_QUALIFY_STATUS.QUALIFY)
      return null;
    if ((user.loanInfo?.monthlyIncome ?? 0) <= 0) return null;

    const approved =
      user.approveStatus === USER_APPROVE_STATUS.APPROVED && user.approvalAmount
        ? user.approvalAmount
        : undefined;

    const candidates = [
      Number.isFinite(preQualPower) && preQualPower > 0
        ? preQualPower
        : undefined,
      approved,
    ].filter((n): n is number => typeof n === 'number');

    if (candidates.length === 0) return null;
    return Math.min(...candidates);
  }, [user, preQualPower]);

  if (!user) return null;

  if (!qualified) {
    return (
      <Chip
        label="Get pre-qualified →"
        size="small"
        onClick={() => navigate('/loan/qualifications')}
        sx={{
          fontWeight: 600,
          cursor: 'pointer',
          color: theme.palette.mainColors.primary,
          borderColor: theme.palette.mainColors.primary,
          backgroundColor: 'transparent',
          border: `1px solid ${theme.palette.mainColors.primary}`,
        }}
        variant="outlined"
      />
    );
  }

  if (qualifiedBuyingPower == null) {
    return null;
  }

  const powerLabel = `Buying power: ${formatUsd(qualifiedBuyingPower)}`;

  const handleOpen = () => {
    cancelClose();
    setOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          flexShrink: 0,
          width: CHIP_SLOT_WIDTH,
          minWidth: CHIP_SLOT_WIDTH,
          maxWidth: CHIP_SLOT_WIDTH,
        }}
      >
        <Chip
          size="small"
          variant="outlined"
          title={phase === 0 ? powerLabel : insightShort}
          onMouseEnter={handleOpen}
          onMouseLeave={scheduleClose}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
            cancelClose();
          }}
          label={
            <Box
              component="span"
              sx={{
                position: 'relative',
                display: 'block',
                width: CHIP_SLOT_WIDTH - 24,
                height: 20,
                mx: 'auto',
              }}
            >
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: phase === 0 ? 1 : 0,
                  // Fade the outgoing label out before the incoming one fades
                  // in — a crossfade showed both texts on top of each other.
                  transition:
                    phase === 0
                      ? 'opacity 0.25s ease-in 0.25s'
                      : 'opacity 0.25s ease-out',
                }}
              >
                {powerLabel}
              </Box>
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  opacity: phase === 1 ? 1 : 0,
                  // Fade the outgoing label out before the incoming one fades
                  // in — a crossfade showed both texts on top of each other.
                  transition:
                    phase === 1
                      ? 'opacity 0.25s ease-in 0.25s'
                      : 'opacity 0.25s ease-out',
                }}
              >
                {insightShort}
              </Box>
            </Box>
          }
          sx={{
            width: '100%',
            height: CHIP_SLOT_HEIGHT,
            fontWeight: 600,
            color: '#1D9E75',
            borderColor: '#1D9E75',
            backgroundColor: 'rgba(29, 158, 117, 0.08)',
            cursor: 'pointer',
            '& .MuiChip-label': {
              width: '100%',
              px: 1,
              overflow: 'hidden',
            },
          }}
        />
        <Popper
          open={open}
          anchorEl={containerRef.current}
          placement="bottom-end"
          disablePortal
          transition
          sx={{ zIndex: theme.zIndex.modal }}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
              <Paper
                elevation={12}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                sx={{
                  mt: 0.5,
                  p: 2,
                  maxWidth: 'min(calc(100vw - 32px), 520px)',
                  maxHeight: 'min(85vh, 680px)',
                  overflow: 'auto',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <RateCommandCenterPanel presentation="popoverBody" />
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
